import AddBoxIcon from "@mui/icons-material/AddBox";
import type MenuItem from "@mui/material/MenuItem";
import { Observer } from "mobx-react-lite";
import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import type { InventoryRecord } from "../../../stores/definitions/InventoryRecord";
import ContainerModel from "../../../stores/models/ContainerModel";
import { match } from "../../../util/Util";
import ContextMenuAction, { type ContextMenuRenderOptions } from "./ContextMenuAction";
import CreateDialog from "./CreateDialog";

type CreateActionArgs = {
  as: ContextMenuRenderOptions;
  disabled: string;
  selectedResults: Array<InventoryRecord>;
  closeMenu: () => void;
};

const CreateAction = forwardRef<React.ElementRef<typeof MenuItem>, CreateActionArgs>(
  ({ as, disabled, selectedResults, closeMenu }, ref) => {
    const { t } = useTranslation(["inventory", "common"]);
    const [openCreateDialog, setOpenCreateDialog] = React.useState(false);

    const isFullContainer = (): boolean =>
      Boolean(selectedResults[0] instanceof ContainerModel && selectedResults[0].isFull);

    const onClick = () => {
      setOpenCreateDialog(true);
    };

    const onCloseHandler = () => {
      setOpenCreateDialog(false);
      closeMenu();
    };

    const disabledHelp = match<void, string>([
      [() => disabled !== "", disabled],
      [() => selectedResults.length === 0, t("contextMenu.create.nothingSelected")],
      [() => selectedResults.length > 1, t("contextMenu.create.singleItemOnly")],
      [() => !selectedResults[0].permittedActions.has("READ"), t("contextMenu.create.noPermission")],
      [() => isFullContainer(), t("contextMenu.create.containerFull")],
      [() => true, ""],
    ])();

    return (
      <Observer>
        {() => (
          <ContextMenuAction
            onClick={onClick}
            icon={<AddBoxIcon />}
            label={t("common:actions.create")}
            disabledHelp={disabledHelp}
            as={as}
            ref={ref}
          >
            <CreateDialog
              // the key causes the local state to be cleared when closing
              key={openCreateDialog ? 1 : 0}
              open={openCreateDialog}
              onClose={onCloseHandler}
              existingRecord={selectedResults[0]}
            />
          </ContextMenuAction>
        )}
      </Observer>
    );
  },
);

CreateAction.displayName = "CreateAction";
export default CreateAction;
